<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { NButton, NSkeleton } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useSourceActivityStore } from '../stores/source_activity'
import IngestActivityRow from './IngestActivityRow.vue'

// The activity-list half of AddToWikiDrawer.vue (#1099) — shared
// across all three tabs (Note/File/URL), so it owns its own subscription
// to the source_activity store rather than receiving rows as props.
const emit = defineEmits<{
  (e: 'open-path', path: string): void
  (e: 'open-log'): void
}>()

const activity = useSourceActivityStore()
const { active, recentTerminal, loading: activityLoading, loadError: activityError, queueDepth } = storeToRefs(activity)

// Cap the "Active" list to a screenful; anything beyond that collapses
// into an "and N more running…" line instead of growing the drawer.
const MAX_VISIBLE_ACTIVE = 5

const pagedActive = computed(() => active.value.slice(0, MAX_VISIBLE_ACTIVE))
const overflowCount = computed(() => Math.max(0, active.value.length - MAX_VISIBLE_ACTIVE))

// "Full log" opens the activity log view (/log); the drawer handles the
// navigation so it can close on the way, as it does for page links.
function openFullLog(): void {
  emit('open-log')
}

onMounted(() => {
  void activity.load()
})
</script>

<template>
  <section
    class="mt-6 flex flex-col gap-3 border-t border-border-divider pt-5"
    aria-labelledby="ingest-activity-heading"
    data-testid="activity-list"
  >
    <div class="flex items-center gap-2">
      <h3 id="ingest-activity-heading" class="m-0 flex-1 text-sm font-semibold text-ink">Ingest activity</h3>
      <a
        href="/log"
        class="inline-flex min-h-10 items-center text-[13px] text-brand-primary underline"
        data-testid="activity-full-log"
        @click.prevent="openFullLog"
        >Full log</a
      >
    </div>

    <div
      v-if="activityLoading && !active.length && !recentTerminal.length"
      class="flex flex-col gap-2"
      data-testid="activity-skeleton"
    >
      <NSkeleton :repeat="2" class="!h-12 !rounded-xl" />
    </div>

    <div v-else-if="activityError" class="flex flex-col gap-2 text-sm text-status-alert" data-testid="activity-error">
      <span>Couldn't load recent activity: {{ activityError }}</span>
      <NButton size="small" class="min-h-10 self-start" @click="activity.load()">Retry</NButton>
    </div>

    <template v-else>
      <section v-if="active.length" class="flex flex-col gap-2" data-testid="activity-active">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
          Active ({{ active.length }})
        </h4>
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
          <IngestActivityRow
            v-for="r in pagedActive"
            :key="r.id"
            :row="r"
            :queue-position="activity.queuePositionFor(r.id)"
            :queue-depth="queueDepth"
            @open-path="emit('open-path', $event)"
          />
        </ul>
        <p v-if="overflowCount > 0" class="m-0 text-xs text-ink-faint">and {{ overflowCount }} more running…</p>
      </section>

      <section v-if="recentTerminal.length" class="flex flex-col gap-1" data-testid="activity-recent">
        <h4 class="m-0 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">Recent (last 24h)</h4>
        <ul class="m-0 flex list-none flex-col p-0">
          <IngestActivityRow v-for="r in recentTerminal" :key="r.id" :row="r" @open-path="emit('open-path', $event)" />
        </ul>
      </section>

      <p v-if="!active.length && !recentTerminal.length" class="m-0 text-sm text-ink-muted">
        Nothing in flight, and no activity in the last 24h.
      </p>
    </template>
  </section>
</template>
